import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';
import { getIDFromUrl } from '@rossum/api-client';
import { Hook, RUN_STATUS_OPTIONS } from '@rossum/api-client/hooks';
import { Queue } from '@rossum/api-client/queues';
import { Workspace } from '@rossum/api-client/workspaces';
import {
  FilterDateRange,
  FilterMultiSelect,
  FilterNumberValues,
} from '@rossum/components/filtering/components';
import { createSettings } from '@rossum/components/filtering/helpers';
import {
  convertFromUTC,
  convertToUTC,
  formatDateHoursSecondsToQuery,
  formatDateTimeWithSecondsPickerValue,
} from '../../../../../lib/dateUtils';

export const activitiesQueryParser = z.object({
  startAfter: z.coerce.string().optional(),
  startBefore: z.coerce.string().optional(),
  endAfter: z.coerce.string().optional(),
  endBefore: z.coerce.string().optional(),
  search: z.coerce.string().optional(),
  page: z.coerce.number().optional(),
  pageSize: z.coerce.number().optional(),
  hook: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  annotation: z.coerce
    .string()
    .transform(val => val.split(',').map(Number))
    .pipe(z.number().array())
    .optional()
    .catch(undefined),
  status: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.enum(RUN_STATUS_OPTIONS).array())
    .optional()
    .catch(undefined),
  queue: z.coerce
    .string()
    .transform(val => val.split(','))
    .pipe(z.string().array())
    .optional()
    .catch(undefined),
});

export type ActivitiesQuery = Partial<z.infer<typeof activitiesQueryParser>>;

export const useActivitiesFilterSettings = ({
  extensions,
  isLoadingExtensions,
  queues,
  workspaces,
  isQueuesLoading,
  setFilters,
}: {
  extensions: Hook[] | undefined;
  isLoadingExtensions: boolean;
  queues: Queue[] | undefined;
  workspaces: Workspace[] | undefined;
  isQueuesLoading: boolean;
  setFilters: (state: ActivitiesQuery) => void;
}) => {
  const intl = useIntl();

  const workspaceOptions = useMemo(() => {
    return workspaces
      ? workspaces
          .map(w => ({
            id: w.id.toString(),
            label: w.name,
            options: w.queues.map(q => getIDFromUrl(q).toString()),
          }))
          .filter(ws => ws.options.length > 0)
      : undefined;
  }, [workspaces]);

  return [
    createSettings({
      filterKey: 'status',
      options: [...RUN_STATUS_OPTIONS],
      optionsAreLoading: false,
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.status.label',
      }),
      getChipLabel: value =>
        [...RUN_STATUS_OPTIONS].filter(o => value.includes(o)).join(', '),
      valueGetter: (currentValue: ActivitiesQuery) => currentValue?.status,
      setFilter: value => setFilters({ status: value, page: 1 }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'hook',
      options: extensions?.map(h => h.id.toString()) ?? [],
      optionsAreLoading: isLoadingExtensions,
      getOptionLabel: option => {
        const hook = extensions?.find(h => h.id === Number(option));
        const { id, name } = hook ?? { id: '', name: '' };

        return `${id}: ${name}`;
      },
      getChipLabel: value =>
        extensions
          ?.filter(e => value?.includes(e.id))
          .map(e => e.name)
          .join(', ') ?? '',
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.source.label',
      }),
      valueGetter: (currentValue: ActivitiesQuery) => currentValue?.hook,
      setFilter: value => setFilters({ hook: value, page: 1 }),
      Component: FilterMultiSelect,
    }),
    createSettings({
      filterKey: 'start',
      options: [],
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.extension.start',
      }),
      getChipLabel: v =>
        v[0] && v[1]
          ? formatDateTimeWithSecondsPickerValue(v[0], v[1], intl.locale)
          : 'N/A',
      valueGetter: ({ startAfter, startBefore }: ActivitiesQuery) => {
        if (!startAfter || !startBefore) return undefined;

        const beginDate = startAfter
          ? convertFromUTC(new Date(startAfter))
          : null;
        const endDate = startBefore
          ? convertFromUTC(new Date(startBefore))
          : null;

        return [beginDate, endDate] as [Date | null, Date | null];
      },
      setFilter: value => {
        if (!value)
          return setFilters({
            startAfter: undefined,
            startBefore: undefined,
            page: 1,
          });

        const [from, to] = value;

        return setFilters({
          startAfter: from
            ? formatDateHoursSecondsToQuery(convertToUTC(from))
            : undefined,
          startBefore: to
            ? formatDateHoursSecondsToQuery(convertToUTC(to))
            : undefined,
          page: 1,
        });
      },
      Component: props => (
        <FilterDateRange {...props} isRange locale={intl.locale} />
      ),
    }),
    createSettings({
      filterKey: 'end',
      options: [],
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.extension.end',
      }),
      getChipLabel: v =>
        v[0] && v[1]
          ? formatDateTimeWithSecondsPickerValue(v[0], v[1], intl.locale)
          : 'N/A',
      valueGetter: ({ endAfter, endBefore }: ActivitiesQuery) => {
        if (!endAfter || !endBefore) return undefined;

        const beginDate = endAfter ? convertFromUTC(new Date(endAfter)) : null;
        const endDate = endBefore ? convertFromUTC(new Date(endBefore)) : null;

        return [beginDate, endDate] as [Date | null, Date | null];
      },
      setFilter: value => {
        if (!value)
          return setFilters({
            endAfter: undefined,
            endBefore: undefined,
            page: 1,
          });

        const [from, to] = value;

        return setFilters({
          endAfter: from
            ? formatDateHoursSecondsToQuery(convertToUTC(from))
            : undefined,
          endBefore: to
            ? formatDateHoursSecondsToQuery(convertToUTC(to))
            : undefined,
          page: 1,
        });
      },
      Component: props => (
        <FilterDateRange {...props} isRange locale={intl.locale} />
      ),
    }),
    createSettings({
      filterKey: 'annotation',
      options: [],
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.annotations.label',
      }),
      getChipLabel: value => value.join(', '),
      valueGetter: (currentValue: ActivitiesQuery) => currentValue?.annotation,
      setFilter: value => setFilters({ annotation: value, page: 1 }),
      Component: FilterNumberValues,
    }),
    createSettings({
      filterKey: 'queue',
      options: workspaceOptions ?? [],
      optionsAreLoading: isQueuesLoading,
      name: intl.formatMessage({
        id: 'containers.settings.extensions.logs.filters.queue.label',
      }),
      getOptionLabel: (option: string) =>
        queues?.find(q => q.id === Number(option))?.name ?? '',
      getChipLabel: (value: string[]) =>
        value
          .map(v => queues?.find(q => q.id === Number(v))?.name ?? '')
          .filter(Boolean)
          .join(', '),
      valueGetter: (currentValue: ActivitiesQuery) => currentValue?.queue,
      validateFilter: (value: string[]) =>
        !value.every(v => queues?.map(q => q.id.toString()).includes(v)),
      setFilter: (value: string[] | undefined) => setFilters({ queue: value }),
      Component: FilterMultiSelect,
    }),
  ];
};
